import React from 'react';

import MenuItem from '~/components/core/Atomic/MenuItem';

import { TextFieldFormik as TextFieldFormikUntyped } from '../../TextFieldFormik';

import type { HolidaySubdivision, TextFieldFormikSelectProps } from './types';

import { useStyles } from '../../../assets/styles';

// Assertion over a still-untyped JS component, which the TS rule allows for gradual migration: it
// only narrows the props this file passes, so a wrong prop name or type is still a compile error.
const TextFieldFormik = TextFieldFormikUntyped as React.ComponentType<TextFieldFormikSelectProps>;

// The select needs a concrete value for "no subdivision", and the PUT needs null. Empty string is
// the bridge: Formik holds it, and both submit handlers normalise it back to null.
export const NO_HOLIDAY_SUBDIVISION = '';

interface HolidaySubdivisionSelectFormikProps {
  id: string;
  territory?: string;
  subdivisions: HolidaySubdivision[];
  isLoading?: boolean;
  isError?: boolean;
}

function HolidaySubdivisionSelectFormik({
  id,
  territory,
  subdivisions,
  isLoading,
  isError,
}: HolidaySubdivisionSelectFormikProps): React.ReactElement {
  const { classes } = useStyles();
  const hasSubdivisions = subdivisions.length > 0;

  let helperText = `${territory || 'This territory'} has no holiday subdivisions — the country-level calendar applies.`;
  if (hasSubdivisions) {
    helperText = 'Bank holidays for this subdivision drive every business-day deadline (Five Sigma only).';
  } else if (isError) {
    // An empty option list after a failed fetch means nothing about the territory, so it must not
    // read as "no subdivisions exist".
    helperText = 'The holiday subdivisions could not be loaded.';
  }

  return (
    <TextFieldFormik
      id={id}
      label="Holiday Subdivision"
      className={classes.textField}
      select
      // Committing a subdivision the user never picked would silently move every business-day
      // deadline the calendar drives, so the single-option shortcut is off here.
      autoSelectSingleOption={false}
      disabled={isLoading || !hasSubdivisions}
      helperText={helperText}
      fullWidth
    >
      <MenuItem value={NO_HOLIDAY_SUBDIVISION}>None (country-level)</MenuItem>
      {subdivisions.map(({ code, name }) => (
        <MenuItem key={code} value={code}>
          {name}
        </MenuItem>
      ))}
    </TextFieldFormik>
  );
}

export default HolidaySubdivisionSelectFormik;
